import { elements } from "@/data/elements";

/**
 * Compact 18×7 periodic-table grid used in Element Battle's setup preview
 * and round-recap "Coverage" panel.
 *
 * Cells are rendered for periods 1–7 main grid only (no f-block tail) - the
 * goal is at-a-glance pool visualization, not a fully labelled reference.
 */
export type CellState = "highlight" | "named" | "missed" | "muted";

export default function MiniPeriodicTable({
  /** Map of atomic number → cell state. Numbers not in the map are "muted". */
  states,
  /** Visually hide this from screen readers - it's decorative. */
  ariaLabel,
}: {
  states: ReadonlyMap<number, CellState>;
  ariaLabel?: string;
}) {
  return (
    <div
      className="rounded-md border border-border bg-background p-3 overflow-x-auto"
      role={ariaLabel ? "img" : undefined}
      aria-label={ariaLabel}
    >
      <div
        className="grid gap-[3px] mx-auto"
        style={{
          gridTemplateColumns: "repeat(18, minmax(0, 1fr))",
          minWidth: "320px",
          maxWidth: "560px",
        }}
        aria-hidden={ariaLabel ? undefined : "true"}
      >
        {Array.from({ length: 7 * 18 }).map((_, i) => {
          const row = Math.floor(i / 18) + 1;
          const col = (i % 18) + 1;
          const el = elements.find(
            (e) => e.row === row && e.col === col && e.row <= 7,
          );
          if (!el) {
            return <div key={i} className="aspect-square" />;
          }
          const state = states.get(el.number) ?? "muted";
          const cls = cellClass(state);
          return (
            <div
              key={i}
              className={`aspect-square rounded-[2px] flex items-center justify-center text-[8px] font-semibold border transition-colors ${cls}`}
            >
              {state === "muted" ? "" : el.symbol}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function cellClass(state: CellState): string {
  switch (state) {
    case "highlight":
      return "bg-primary/15 border-primary/60 text-primary";
    case "named":
      return "bg-primary/80 border-primary text-primary-foreground";
    case "missed":
      return "bg-destructive/15 border-destructive/60 text-destructive";
    case "muted":
    default:
      return "bg-muted/40 border-border/40 text-transparent";
  }
}
